import { useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { getCardsRequest, getCardsReset, resetAddNewCard } from '../../../actions/cardActions';
import { getVehicleTypesAction } from 'actions/storeActions';
import dayjs from 'dayjs';
import { DATE_FORMATS, DATE_FORMAT_HYPHEN } from 'utilities/constants';
import { FilterValues } from './useCardsFilters';

/**
 * Cards Data Hook
 *
 * Manages data fetching lifecycle, global search handling, and data refresh
 * after CRUD operations. Encapsulates all side effects related to data loading.
 *
 * @param filterValues - Current filter values
 * @param setFilterValues - Filter state setter
 * @param resetSelection - Callback to reset selection
 * @param modalType - Optional modal type
 * @param contractNumber - Contract number for modal mode
 * @param accountLevel - Account level
 * @param assignedAccountNumber - Assigned account number
 * @param assignedGrandfatherClientNumber - Grandfather client number
 * @param vehicleTypes - Vehicle types from store
 * @param isCardsLoaded - Whether cards are loaded
 * @param isCardNumberAdded - Whether card number was added
 * @param globalSearchLoaded - Global search loaded flag
 * @param globalSearchResult - Global search result
 * @param globalSearchType - Global search type
 * @param cardResults - Current card results
 * @param transferSingleCardLoaded - Transfer single card success flag
 * @param transferMultipleCardLoaded - Transfer multiple cards success flag
 * @param activateSingleCardLoaded - Activate single card success flag
 * @param activateMultipleCardLoaded - Activate multiple cards success flag
 * @param createCardLoaded - Create card success flag
 * @param editCardLoaded - Edit card success flag
 * @param editMultipleCardLoaded - Edit multiple cards success flag
 * @param updateSingleCardStatusLoaded - Update single card status success flag
 * @param updateMultipleCardStatusLoaded - Update multiple card status success flag
 * @param handleRowSelect - Callback to handle row selection
 * @param setFilterDisplay - Callback to set filter display
 * @returns Data operations
 */

export interface CardsDataOperations {
  getCardsList: () => void;
  getCardsRequestPayload: () => any;
}

export const useCardsData = ({
  filterValues,
  setFilterValues,
  resetSelection,
  modalType,
  contractNumber,
  accountLevel,
  assignedAccountNumber,
  assignedGrandfatherClientNumber,
  vehicleTypes,
  isCardsLoaded,
  isCardNumberAdded,
  globalSearchLoaded,
  globalSearchResult,
  globalSearchType,
  cardResults,
  transferSingleCardLoaded,
  transferMultipleCardLoaded,
  activateSingleCardLoaded,
  activateMultipleCardLoaded,
  createCardLoaded,
  editCardLoaded,
  editMultipleCardLoaded,
  updateSingleCardStatusLoaded,
  updateMultipleCardStatusLoaded,
  handleRowSelect,
  setFilterDisplay,
  resetFilterValues,
}: {
  filterValues: FilterValues;
  setFilterValues: (values: FilterValues) => void;
  resetSelection: () => void;
  resetFilterValues: (isFilterClick?: boolean, triggerFilter?: boolean) => void;
  modalType?: string;
  contractNumber?: string;
  accountLevel?: string;
  assignedAccountNumber?: string;
  assignedGrandfatherClientNumber?: string;
  vehicleTypes: any[];
  isCardsLoaded: boolean;
  isCardNumberAdded: boolean;
  globalSearchLoaded: boolean;
  globalSearchResult: string;
  globalSearchType: string;
  cardResults: any[];
  transferSingleCardLoaded: boolean;
  transferMultipleCardLoaded: boolean;
  activateSingleCardLoaded: boolean;
  activateMultipleCardLoaded: boolean;
  createCardLoaded: boolean;
  editCardLoaded: boolean;
  editMultipleCardLoaded: boolean;
  updateSingleCardStatusLoaded: boolean;
  updateMultipleCardStatusLoaded: boolean;
  handleRowSelect: (card: any) => void;
  setFilterDisplay: (display: boolean) => void;
}): CardsDataOperations => {
  const dispatch = useDispatch();
  const isMounted = useRef(false);
  const lastFetchedModal = useRef<string>('');

  // useRef pattern for stable callbacks (prevents infinite loops)
  const handleRowSelectRef = useRef(handleRowSelect);
  const setFilterDisplayRef = useRef(setFilterDisplay);
  const resetFilterValuesRef = useRef(resetFilterValues);

  useEffect(() => {
    handleRowSelectRef.current = handleRowSelect;
    setFilterDisplayRef.current = setFilterDisplay;
    resetFilterValuesRef.current = resetFilterValues;
  });

  /**
   * Build request payload for cards API
   * Pure function - no side effects
   */
  const getCardsRequestPayload = useCallback(() => {
    return {
      contractNumber: modalType
        ? contractNumber
        : filterValues?.cfAccountNumber !== null
          ? filterValues.cfAccountNumber
          : assignedAccountNumber,
      parent: assignedGrandfatherClientNumber,
      startRange: filterValues?.cfStartRange,
      endRange: filterValues?.cfEndRange,
      accountNumber: modalType ? contractNumber : '',
      status: filterValues?.cfStatus,
      issueDateFrom: filterValues?.cfIssueDateFrom
        ? dayjs(filterValues?.cfIssueDateFrom, DATE_FORMATS).format(DATE_FORMAT_HYPHEN)
        : '',
      issueDateTo: filterValues?.cfIssueDateTo
        ? dayjs(filterValues?.cfIssueDateTo, DATE_FORMATS).format(DATE_FORMAT_HYPHEN)
        : '',
      driverName: filterValues?.cfDriverName?.toUpperCase(),
      vehicleRegNumber: filterValues?.cfVehicleRegNumber?.toUpperCase(),
      domesticNumber: filterValues?.cfDomesticNumber?.toUpperCase(),
      costCentre: filterValues?.cfCostCentre?.toUpperCase(),
      page: filterValues?.pageNumber,
      pageSize: filterValues?.pageSize,
      sortColumn: filterValues?.sortColumn,
      sortDirection: filterValues?.sortDirection,
    };
  }, [modalType, contractNumber, filterValues, assignedAccountNumber, assignedGrandfatherClientNumber]);

  /**
   * Fetch cards list from API
   * Main data fetching operation
   * @param skipFilterUpdate - Skip setFilterValues call (used for modal initial fetch to prevent unmount/remount)
   */
  const getCardsList = useCallback(
    (skipFilterUpdate = false) => {
      // Business rule: do not fetch cards for non-C account levels in modal mode
      if (modalType && accountLevel !== 'C') return;

      const payload = getCardsRequestPayload();

      resetSelection();

      if (isCardNumberAdded) {
        dispatch(getCardsRequest({ ...payload, sortColumn: 'CardNumber' }, filterValues?.isFilterClick));
        dispatch(resetAddNewCard());
        if (!skipFilterUpdate) {
          setFilterValues({
            ...filterValues,
            pageNumber: 1,
            triggerFilter: false,
            isFilterClick: false,
          });
        }
      } else {
        dispatch(getCardsRequest(payload, filterValues?.isFilterClick));
        if (!skipFilterUpdate) {
          setFilterValues({
            ...filterValues,
            triggerFilter: false,
            isFilterClick: false,
          });
        }
      }
    },
    [
      modalType,
      accountLevel,
      getCardsRequestPayload,
      resetSelection,
      isCardNumberAdded,
      dispatch,
      filterValues,
      setFilterValues,
    ]
  );

  /**
   * EFFECT: Initial mount - Load vehicle types and cleanup on unmount
   * Also resets filter display and filter state when the account changes.
   */
  useEffect(() => {
    if (assignedAccountNumber) {
      // Load vehicle types if not already loaded
      if (vehicleTypes.length === 0) {
        dispatch(getVehicleTypesAction());
      }

      // Reset selection, filter display, and filter values when account changes.
      // IMPORTANT: triggerFilter is always false here. In modal mode, the
      // [modalType, contractNumber, accountLevel] effect fires getCardsList(true)
      // on the same render cycle. Passing triggerFilter=true here would cause the
      // [filterValues] effect to also fire getCardsList(), producing a double-fetch.
      resetSelection();
      setFilterDisplayRef.current?.(!modalType);
      resetFilterValuesRef.current?.(!!modalType, false);
    }

    // Cleanup on unmount
    return () => {
      dispatch(getCardsReset());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedAccountNumber]); // Only run when account changes

  /**
   * EFFECT: Initial data fetch for modal mode
   * Business rule: Only fetch cards for account level 'C' in modal mode
   * Bug fix: Track which modal we've fetched to prevent infinite loop
   * Runs when modalType/contractNumber/accountLevel change, but only fetches once per unique modal
   * skipFilterUpdate=true prevents component unmount/remount that was resetting the ref
   */
  useEffect(() => {
    if (modalType && contractNumber && accountLevel === 'C') {
      const modalKey = `${modalType}-${contractNumber}`;

      // Only fetch if we haven't fetched this specific modal yet
      if (lastFetchedModal.current !== modalKey) {
        lastFetchedModal.current = modalKey;
        getCardsList(true); // Skip setFilterValues to prevent component from unmounting/remounting
      }
    }
    // Note: getCardsList is intentionally NOT in deps to prevent infinite loop
    // The ref ensures we only fetch once per unique modal instance
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalType, contractNumber, accountLevel]);

  /**
   * EFFECT: Global search handling
   * Legitimate effect: Responding to external data change (global search result)
   */
  useEffect(() => {
    if (globalSearchLoaded && globalSearchResult) {
      // Update filter values based on search type
      switch (globalSearchType) {
        case 'Registration':
          setFilterValues({
            ...filterValues,
            cfVehicleRegNumber: globalSearchResult,
            cfStartRange: '',
            cfEndRange: '',
          });
          break;
        default:
          setFilterValues({
            ...filterValues,
            cfVehicleRegNumber: '',
            cfStartRange: globalSearchResult,
            cfEndRange: globalSearchResult,
          });
          break;
      }

      // Auto-select single card result
      if (cardResults.length === 1) {
        window.dataLayer.push({
          event: 'Invidual_card_select',
          selected: cardResults[0].cardNumber,
          account_number: cardResults[0].selectedAccount,
        });
        handleRowSelectRef.current?.(cardResults[0]);
      }
    } else if (isMounted.current && !globalSearchLoaded && globalSearchResult) {
      // Clear search filters on failure
      setFilterValues({
        ...filterValues,
        cfVehicleRegNumber: '',
        cfStartRange: '',
        cfEndRange: '',
        triggerFilter: false,
      });
      setFilterDisplayRef.current?.(false);
    }

    isMounted.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalSearchLoaded]); // Only run when search completes

  /**
   * EFFECT: Trigger fetch when filters change
   * Legitimate effect: Responding to user action (filter submission)
   */
  useEffect(() => {
    if (filterValues?.triggerFilter) {
      getCardsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues]); // Run when filter values change

  /**
   * EFFECT: Refresh data after CRUD operations
   * Legitimate effect: Data synchronization after mutations
   */
  useEffect(() => {
    if (
      isCardsLoaded &&
      (transferSingleCardLoaded ||
        transferMultipleCardLoaded ||
        activateSingleCardLoaded ||
        activateMultipleCardLoaded ||
        createCardLoaded ||
        editCardLoaded ||
        editMultipleCardLoaded ||
        updateSingleCardStatusLoaded ||
        updateMultipleCardStatusLoaded)
    ) {
      getCardsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    transferSingleCardLoaded,
    transferMultipleCardLoaded,
    activateSingleCardLoaded,
    activateMultipleCardLoaded,
    createCardLoaded,
    editCardLoaded,
    editMultipleCardLoaded,
    updateSingleCardStatusLoaded,
    updateMultipleCardStatusLoaded,
  ]); // Run when any CRUD operation completes

  return {
    getCardsList,
    getCardsRequestPayload,
  };
};
